/**
 * Main application module required for proper functioning
 * Handles default functionality like authentication, alerts
 * view state and current data state
 *
 * @module app
 */

import React, {Suspense} from 'react';
import ReactDOM from 'react-dom/client';

const AppComponent = React.lazy(() =>
	import(
		/* webpackChunkName: "App-Component" */ '@netx/core-app-next/components/App'
	),
);

const UnauthenticatedLandingComponent = React.lazy(() =>
	import(
		/* webpackChunkName: "UnauthenticatedLanding-Component" */ '@netx/core-auth-next/components/UnauthenticatedLanding'
	),
);

// import semver from 'core-libs/semver.browser';
import {localStore} from '@netx/core-next/instance/localStore';
import {session} from '@netx/core-auth/collections/singleton/session';
import {userPrefs} from '@netx/core-user/collections/singletons/userPreferences';
import {
	currentUserMeetsUserLevel,
	UserLevel,
} from '@netx/core-user-next/utils/currentUserMeetsUserLevel';
import Modernizr from 'core-libs/modernizr/modernizr';

// Bootstrap
// We should cherry pick the things we use and abandon the rest - just make a list of the things core has natively from bootstrap and themes can fill in the rest
import 'core-libs/bootstrap';
// Then our bootstrap overrides/enhancements
import 'core-libs/bootstrap-netx';
import {spawnNewWindow} from '@netx/core-next/utils/browser';

// Models
import CoreModel from '@netx/core/lib/netx-model';
import AppModel from './models/app';
import BatchJobModel from './models/batchJob';
import BrandModel from './models/brand';

// Views
import AlertView from './views/alertView';
// import AppView from './views/appView';
import FullScreenView from './views/appFullScreenView';
import AppProgressView from './views/appProgressView';

// Collections
import OptionTilesCollection from './collections/optionTiles';

import Analytics from '@netx/core/lib/netx-analytics';

// App config `Netx.app.options`
import customConfig from './config';
import authConfig from '@netx/core-auth/config';

// A small package that lets us know when an element (in our case a view's $el) has loaded all contained images
import 'core-libs/imagesloaded.pkgd.js';

import {batchJobs} from './collections/singleton/batchJobs';
import {legacyWebsocket} from './collections/singleton/websocket';
import {getPreference} from '@netx/core-user-next/utils/getPreference';
import {bodyView} from './singletons/bodyViews';
import {fsViews} from './singletons/fsViews';
import {modalView, modalViews} from './singletons/modalViews';
import {ready} from '@netx/core-next/whenReady';
import {
	getPrefixedLogger,
	dispatcher,
	appRouter,
} from '@netx/core-next/instance';
import {setGlobal} from '@netx/core-next/utils/setGlobal';

const logger = getPrefixedLogger('core:app');

//=================================
//	INIT
//=================================
// All collection and model instances and event listeners
// are created when the module is initialized.
/**
 * app:init handler initializes the Netx.app namespace and various other handlers.
 * @event module:app#app:init
 * @param {object} options
 * @fires module:app#app:extend
 * @fires module:app#app:initialized
 */
dispatcher.once('app:init', function (options) {
	logger.silly('initializing...');

	const NS = (Netx.app = Netx.app || new AppModel());
	NS.set({...customConfig, ...options});

	//=================================
	//	CREATE MODELS AND COLLECTIONS
	//=================================

	const disableTouchMove = (e) => {
		e.preventDefault();
	};

	setGlobal({
		disableTouchMove() {
			document.addEventListener('touchmove', disableTouchMove, {
				passive: false,
			});
		},
		enableTouchMove() {
			document.removeEventListener('touchmove', disableTouchMove, {
				passive: false,
			});
		},
		/**
		 * Set user preference
		 * @function
		 * @param {string} key [dot seperated] preference key
		 * @param {mixed} value Value to set on preference
		 */
		setPreference: function (key, val) {
			return userPrefs.setUserPreference(key, val);
		},
	});

	NS.brand = new BrandModel();
	//=================================
	//	!CREATE MODELS AND COLLECTIONS
	//=================================

	// Application-level DOM setup/manipulations:
	// =========================================
	//
	// TODO: Should we have a view for the entire DOM (document)?
	// It wouldn't render, but it could hold all this stuff.
	var $body = (NS.$body = $('body')),
		$document = (NS.$document = $(document)),
		$window = (NS.$window = $(window));
	//=================================
	//	WINDOW LISTENERS
	//=================================
	NS.size = {
		width: $window.innerWidth(),
		height: $window.innerHeight(),
	};

	const tickleSession = _.throttle(() => {
		dispatcher.trigger('session:tickle');
	}, 30000); // every 30 seconds we will try - the session can handle its own limits
	document.addEventListener(
		'keydown',
		() => {
			tickleSession();
		},
		{passive: true, capture: true},
	);

	// Window resize listener dispatches event 'app:resize' after window resize has been static for 100ms
	$window.on(
		'resize',
		_.debounce(function () {
			var size = NS.size,
				h = $window.innerHeight(),
				w = $window.innerWidth();

			if (size.width !== w || size.height !== h) {
				size.width = w;
				size.height = h;
				dispatcher.trigger('app:resize', size);
			}
		}, 100),
	);
	//=================================
	//	!WINDOW LISTENERS
	//=================================

	const root = ReactDOM.createRoot(document.getElementById('wrapper'));

	bodyView.$el.appendTo($body);
	bodyView.render();

	new FullScreenView({
		el: document.querySelector('.nx-app-fullscreen'),
		viewCollection: fsViews,
	}).render();

	modalView.$el.appendTo($body);
	modalView.render();

	//=================================
	//	EVENT LISTENERS
	//=================================

	/**
	 * The app is in a ready state
	 * @event module:app#app:ready
	 * @param {boolean} start Start the app
	 * @fires module:app#app:loaded
	 * @fires module:app#app:start
	 */
	dispatcher.once(
		'app:ready',
		function (start = false) {
			logger.silly('ready', 'start >', start.toString());
			// TODO: this is a hack for now - need to put this in the proper place for the proper time
			!start &&
				root.render(
					<Suspense fallback={<div />}>
						<UnauthenticatedLandingComponent />
					</Suspense>,
					// document.getElementById('wrapper'),
				);

			ready().then(function () {
				start &&
					root.render(
						<Suspense fallback={<div />}>
							<AppComponent />
						</Suspense>,
					); //, document.getElementById('wrapper'));
				logger.silly('loaded and starting');
				dispatcher.trigger('app:loaded', start);
				// Should we start the app (authenticated)
				start && dispatcher.trigger('app:start');
			});
		},
		window,
	);

	/**
	 * App is loaded and user is authenticated
	 * @event module:app#app:start
	 */
	dispatcher.once('app:start', function () {
		// Netx.websocket = legacyWebsocket;
	});

	/**
	 * The app is loaded
	 * @event module:app#app:loaded
	 */
	dispatcher.once('app:loaded', function (authenticated) {
		appRouter.ready = true;
		if (session.get('browserOk') || session.getCookie('browserOk')) {
			// Process the current route and get it all going
			if (authenticated) {
				const hash = localStore.get(authConfig.POST_LOGIN_HASH_NAME);
				if (hash) window.location.hash = hash;
				//Netx.router.navigate(hash, true);
				localStore.set(authConfig.POST_LOGIN_HASH_NAME, '');
			}
			Backbone.history.loadUrl();
		}
	});

	/**
	 * Switch to the Flex interface
	 * @event module:app#app:browser:override:cancelled
	 */
	dispatcher.once('app:browser:override:cancelled', function () {
		// If the user doesn't want to continue we send them to Flex
		window.location.replace('/ImagePortal.jsp');
	});

	/**
	 * Wait for compatiability confirmation
	 * @event module:app#app:compatible:confirmed
	 * @fires netx:app:modules
	 */
	dispatcher.once('app:compatible:confirmed', function () {
		const promises = [];
		// Get storage location info
		// Must be Importer or higher to fetch
		if (currentUserMeetsUserLevel(UserLevel.IMPORTER)) {
			promises.push(
				NS.storageLocations.fetch(),
				NS.storageLocations.getLocationTypes(),
			);
		}

		// When all promises are complete
		Promise.all(promises).then(() => {
			// Promise.all([
			// 	import(/* webpackChunkName: "post-login-app" */ '@netx/core-permissions'),
			// 	import(/* webpackChunkName: "post-login-app" */ '@netx/core-cart'),
			// 	import(/* webpackChunkName: "post-login-app" */ '@netx/core-asset'),
			// 	import(/* webpackChunkName: "post-login-app" */ '@netx/core-user'),
			// 	import(/* webpackChunkName: "post-login-app" */ '@netx/core-category'),
			// 	import(/* webpackChunkName: "post-login-app" */ '@netx/core-search'),
			// 	import(/* webpackChunkName: "post-login-app" */ '@netx/core-share'),
			// ]).then(modules => {
			// 	dispatcher.trigger('netx:load:module', modules.map(m => m.default), {
			// 		initialized() {
			// 			dispatcher.trigger('app:ready', true);
			// 		},
			// 	});
			// });
			dispatcher.trigger('netx:app:modules');
		});
	});

	/**
	 * @event module:auth#auth:session:invalid
	 * @fires module:app#app:ready
	 */
	dispatcher.once('auth:session:invalid', function () {
		// The app is ready to show the login screen
		dispatcher.trigger('app:ready', false);
	});

	/**
	 * Once we find a valid session, test for correct server version.
	 * @event module:auth#auth:session:valid
	 * @fires module:app#app:compatible:confirmed
	 * @fires module:app#app:serverVersionWarning
	 * @fires module:app#app:user:prefs:ready
	 * @listens module:app#app:compatible:confirmed
	 */
	dispatcher.once('auth:session:valid', function () {
		// Fetch the user prefs
		$.when(userPrefs.fetch()).then(function () {
			// Things in the app can start testing against user prefs now
			dispatcher.trigger('app:user:prefs:ready');

			// Wait until authenticated for this
			// If solr is not enabled, warn the user & suggest Flex
			if (!getPreference('searchEngine.solrEnabled', true)) {
				dispatcher.trigger(
					'app:alert',
					i18n.t('i18n.solrNotEnabledMessage'),
					i18n.t('i18n.warning'),
				);
			}

			// Test server version
			NS.fetch().then(function (java_version) {
				dispatcher.trigger('app:compatible:confirmed');
				// var requiredVersion = NS.options.REQUIRED_SERVER_VERSION;
				// if (!_.isUndefined(semver)) {
				// 	if (
				// 		requiredVersion &&
				// 		!semver.satisfies(java_version, requiredVersion)
				// 	) {
				// 		dispatcher.trigger(
				// 			'app:serverVersionWarning',
				// 			java_version,
				// 			requiredVersion,
				// 		);
				// 	} else {
				// 		dispatcher.trigger('app:compatible:confirmed');
				// 	}
				// } else {
				// 	// If the install is missing the semver dependency log the issue and continue without catastrophic failure.
				// 	dispatcher.trigger('app:compatible:confirmed');
				// 	console.warn('Missing semver dependency.');
				// }
			});
		});
	});

	/**
	 * @event module:auth#auth:token:valid
	 * @fires module:auth#auth:token:handle
	 */
	dispatcher.once('auth:token:valid', function () {
		dispatcher.trigger('auth:token:handle');
	});

	/**
	 * @event module:auth#auth:token:invalid
	 * @fires module:auth#auth:token:handle
	 */
	dispatcher.once('auth:token:invalid', function () {
		dispatcher.trigger('auth:token:handle');
	});

	/**
	 * Using a token
	 * Only need a handful of modules for this
	 * @event module:auth#auth:token:handle
	 * @fires module:netx#netx:load:module
	 * @fires module:app#app:ready
	 */
	// Regardless of valid or invalid - the related view will handle messaging
	dispatcher.once('auth:token:handle', function () {
		const m = window.location.hash.match(/(\w+)/i)?.[1] ?? '';
		if (m === 'uploads') {
			Promise.all([
				import(/* webpackChunkName: "upload-requests" */ '@netx/core-asset'),
				import(/* webpackChunkName: "upload-requests" */ '@netx/core-upload'),
				import(/* webpackChunkName: "upload-requests" */ '@netx/core-category'),
			]).then((modules) => {
				dispatcher.trigger(
					'netx:load:module',
					modules.map((m) => m.default),
					{
						initialized() {
							dispatcher.trigger('app:ready', true);
						},
					},
				);
			});
		}
	});

	/**
	 * Displays an alert box to the user
	 * @event module:app#app:alert:show
	 * @param {object} viewOptions View options
	 */
	dispatcher.on(
		'app:alert:show',
		function (viewOptions) {
			modalViews.add({
				viewClass: AlertView,
				showActions: ['app:alert:show'],
				viewOptions: viewOptions,
			});
		},
		window,
	);

	/**
	 * Prepare and display an alert box to the user
	 * @event module:app#app:alert
	 * @param {string} msg Message to be displayed to the user
	 * @param {string} type Type of alert that should be displayed to user corresponds with bootstrap classes 'error','warn','info','success'
	 * @fires module:app#app:alert:show
	 */
	dispatcher.on('app:alert', function (msg, type) {
		var alertClass, av;
		type = type || 'Warning';
		msg =
			msg || 'Sorry, the system has encountered a problem. Please try again.';
		alertClass = type ? 'alert-' + type.toLowerCase() : '';

		dispatcher.trigger('app:alert:show', {
			model: new CoreModel({
				type: type,
				msg: msg,
				alertClass: alertClass ? ' ' + alertClass : '',
			}),
		});
	});

	/**
	 * Action handler for buttons that want to trigger history.back .
	 * @event module:system#app:back
	 */
	dispatcher.on('app:back', function () {
		// Gives us some added benefits
		appRouter.back();
	});

	/**
	 * @event module:app#netx:browser:override:cancelled
	 * @global
	 */
	dispatcher.on('app:browser:override:cancelled', function () {
		// If the user doesn't want to continue we send them to Flex
		window.location.replace('/ImagePortal.jsp');
	});

	/**
	 * If the user cancelled startup due to the server incompatibility,
	 * fall back to the Flex interface.
	 * @event module:app#netx:compatible:cancelled
	 * @global
	 */
	dispatcher.once('app:compatible:cancelled', function () {
		window.location.replace('/ImagePortal.jsp');
	});

	/**
	 * @event module:app#app:confirm:show
	 * @param {object} viewOptions View options
	 */
	dispatcher.on('app:confirm:show', function (viewOptions) {
		import(
			/* webpackChunkName: "view-appConfirm" */ './views/confirmView'
		).then((View) => {
			modalViews.add({
				viewClass: View.default,
				showActions: ['app:confirm:show'],
				viewOptions: viewOptions,
			});
		});
	});
	/**
	 * Triggers a confirmation dialog to be displayed to the user.
	 * The first parameter is an options object used in a new instance of Netx.Views.Confirmview.
	 * All other arguments are passed to the 'args' property of that view, and are then passed
	 * along with the confirmation event it generates.
	 * @event module:app#app:confirm
	 * @param {object} options Options
	 */
	dispatcher.on('app:confirm', function (options) {
		// any other arguments we pass along to the view, which passes them to the event
		dispatcher.trigger(
			'app:confirm:show',
			_.extend(
				{
					args: _.rest(arguments),
				},
				options,
			),
		);
	});

	/**
	 * @event module:app#app:confirm:delete:show
	 * @param {object} viewOptions View options
	 */
	dispatcher.on('app:confirm:delete:show', function (viewOptions) {
		import(
			/* webpackChunkName: "view-appConfirm" */ './views/confirmDelete'
		).then((View) => {
			modalViews.add({
				viewClass: View.default,
				showActions: ['app:confirm:delete:show'],
				viewOptions: viewOptions,
			});
		});
	});

	/**
	 * Triggers a delete confirmation dialog to be displayed to the user.
	 * The first parameter is an options object used in a new instance of Netx.Views.Confirmview.
	 * All other arguments are passed to the 'args' property of that view, and are then passed
	 * along with the confirmation event it generates.
	 * @event module:app#app:confirm:delete
	 * @param {object} options Options
	 * @fires module:app#app:confirm:delete:show
	 */
	dispatcher.on('app:confirm:delete', function (options) {
		// any other arguments we pass along to the view, which passes them to the event
		dispatcher.trigger(
			'app:confirm:delete:show',
			_.extend(
				{
					args: _.rest(arguments),
				},
				options,
			),
		);
	});

	/**
	 * Confirmation for abandoning unsaved changes
	 * @event module:app#app:confirmUnsaved
	 * @param {object} opts Options for confirmation
	 * @fires module:app#app:confirm
	 * @listens module:app#app:confirmUnsaved:cancelled
	 * @listens module:app#app:confirmUnsaved:confirmed
	 */
	dispatcher.on('app:confirmUnsaved', function (opts) {
		opts = opts || {};

		dispatcher.off('app:confirmUnsaved:confirmed');
		_.isFunction(opts.confirmed) &&
			dispatcher.on('app:confirmUnsaved:confirmed', opts.confirmed);

		dispatcher.off('app:confirmUnsaved:cancelled');
		_.isFunction(opts.cancelled) &&
			dispatcher.on('app:confirmUnsaved:cancelled', opts.cancelled);

		dispatcher.trigger('app:confirm', {
			eventStr: 'app:confirmUnsaved',
			message: i18n.t('i18n.anyUnsavedChangesWillBeLostConfirm'),
			oklabel: i18n.t('i18n.btnYes'),
			cancellabel: i18n.t('i18n.btnNo'),
		});
	});

	/**
	 * Launch the contact dialog
	 * @event module:app#app:contact:show
	 * @param {object} viewOptions View options
	 */
	dispatcher.on(
		'app:contact:show',
		function (viewOptions) {
			import(/* webpackChunkName: "view-contact" */ './views/contact').then(
				(View) => {
					modalViews.add({
						viewClass: View.default,
						showActions: ['app:contact:show'],
						viewOptions: viewOptions,
					});
				},
			);
		},
		window,
	);
	/**
	 * @event module:app#app:contact
	 * @fires module:app#app:contact:show
	 */
	dispatcher.on('app:contact', function () {
		dispatcher.trigger('app:contact:show');
	});
	/**
	 * @event module:app#app:contact:confirmed
	 * @fires module:app#app:contact:error
	 * @fires module:app#app:contact:success
	 */
	dispatcher.on('app:contact:confirmed', function (values, view) {
		Netx.app.contact(values).then(
			function () {
				dispatcher.trigger('app:contact:success');
			},
			function (err) {
				dispatcher.trigger('app:contact:error', err);
			},
		);
	});
	/**
	 * @event module:app#app:contact:success
	 * @fires module:notification#notification
	 */
	dispatcher.on(
		'app:contact:success',
		function () {
			dispatcher.trigger('notification', {
				module: 'app',
				title: i18n.t('i18n.email'),
				description: i18n.t('i18n.emailSent'),
				type: 'success',
			});
		},
		window,
	);
	/**
	 * @event module:app#app:contact:error
	 * @param {string} error Error string
	 * @fires module:notification#notification
	 */
	dispatcher.on(
		'app:contact:error',
		function (error) {
			dispatcher.trigger('notification', {
				module: 'app',
				title: i18n.t('i18n.email'),
				description: i18n.t('i18n.thereWasAnErrorSendingYourRequest'),
				type: 'danger',
			});
		},
		window,
	);

	/**
	 * @event module:app#app:help
	 * @fires module:app#app:help:error
	 */
	dispatcher.on('app:help', function () {
		const url = Netx.getPreference('header.helpUrl');
		if (url != null) {
			// eslint-disable-next-line security/detect-unsafe-regex
			const regexp =
				/(ftp|http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-/]))?/;
			if (regexp.test(url)) return spawnNewWindow(url);

			dispatcher.trigger(
				'app:help:error',
				new Error(i18n.t('i18n.invalidHelpUrl', {url: url})),
			);
			return;
		}
		dispatcher.trigger(
			'app:help:error',
			new Error(i18n.t('i18n.helpUrlNotSet')),
		);
	});
	/**
	 * @event module:app#app:help:error
	 * @param {string} error Error string
	 * @fires module:app#app:alert
	 */
	dispatcher.on('app:help:error', function (error) {
		dispatcher.trigger('app:alert', error, i18n.t('i18n.error'));
	});

	/**
	 * @event module:app#app:progress:show
	 * @param {object} viewOptions View options
	 */
	dispatcher.on(
		'app:progress:show',
		function (viewOptions) {
			modalViews.add({
				viewClass: AppProgressView,
				showActions: ['app:progress:show'],
				viewOptions: viewOptions,
			});
		},
		window,
	);
	/**
	 * @event module:app#app:progress
	 * @param {object} opts Progress options
	 * @fires module:app#app:progress:show
	 */
	dispatcher.on('app:progress', function (opts) {
		// Nothing fancy to do yet
		dispatcher.trigger('app:progress:show', opts);
	});

	/**
	 * @event module:app#app:prompt:show
	 * @param {object} viewOptions View options
	 */
	dispatcher.on('app:prompt:show', function (viewOptions) {
		import(/* webpackChunkName: "view-prompt" */ './views/promptView').then(
			(View) => {
				modalViews.add({
					viewClass: View.default,
					showActions: ['app:prompt:show'],
					viewOptions: viewOptions,
				});
			},
		);
	});
	/**
	 * Triggers a prompt dialog to be displayed to the user.
	 * The first parameter is an options object used in a new instance of Netx.Views.PromptView.
	 * All other arguments are passed to the 'args' property of that view, and are then passed
	 * along with the confirmation event it generates.
	 * @event module:app#app:prompt
	 * @fires module:app#app:prompt:show
	 */
	dispatcher.on('app:prompt', function () {
		// our first argument is options for the ConfirmView
		var opts = Array.prototype.shift.apply(arguments);
		// any other arguments we pass along to the view, which passes them to the event
		dispatcher.trigger(
			'app:prompt:show',
			_.extend({args: Array.prototype.slice.apply(arguments)}, opts),
		);
	});

	/**
	 * @event module:app#app:reportProblem:show
	 * @param {object} viewOptions View options
	 */
	dispatcher.on(
		'app:reportProblem:show',
		function (viewOptions) {
			import(
				/* webpackChunkName: "view-reportProblem" */ './views/problemReportView'
			).then((View) => {
				modalViews.add({
					viewClass: View.default,
					showActions: ['app:reportProblem:show'],
					viewOptions: viewOptions,
				});
			});
		},
		window,
	);
	/**
	 * Prepare and launch the report a problem dialog
	 * @event module:app#app:reportProblem
	 * @fires module:app#app:reportProblem:show
	 */
	dispatcher.on('app:reportProblem', function () {
		dispatcher.trigger('app:reportProblem:show', {
			model: Netx.app,
		});
	});
	/**
	 * @event module:app#app:reportProblem:confirmed
	 * @param {string} description Description of problem
	 * @param {boolean} ccSelf CC reporter
	 */
	dispatcher.on('app:reportProblem:confirmed', function (description, ccSelf) {
		NS.set({
			description: description,
			clientLogs: window.console.history.dump('all'),
			ccSelf: ccSelf ? 'true' : 'false',
		});

		NS.complain().then(
			function () {
				dispatcher.trigger('app:reportProblem:success');
			},
			function (err) {
				dispatcher.trigger('app:reportProblem:error', err);
			},
		);
	});
	/**
	 * @event module:app#app:reportProblem:error
	 * @param {string} error Error string
	 * @fires module:app#app:alert
	 */
	dispatcher.on('app:reportProblem:error', function (error) {
		dispatcher.trigger('app:alert', error, i18n.t('i18n.error'));
	});
	/**
	 * @event module:app#app:reportProblem:success
	 * @fires module:notification#notification
	 */
	dispatcher.on('app:reportProblem:success', function () {
		dispatcher.trigger('notification', {
			module: 'app',
			title: i18n.t('i18n.reportProblem'),
			description: i18n.t('i18n.yourInformationWasSent'),
			type: 'success',
		});
	});

	//============================================
	//	App pager
	//============================================
	/**
	 * Rewind to the first page of a selection
	 * @event module:app#app:pager:firstPage
	 * @param {Netx.Collection} Netx collection instance
	 */
	dispatcher.on('app:pager:firstPage', function (pager, collection) {
		pager.firstPage();
	});

	/**
	 * Forward to the last page of a selection
	 * @event module:app#app:pager:lastPage
	 * @param {Netx.Collection} Netx collection instance
	 */
	dispatcher.on('app:pager:lastPage', function (pager, collection) {
		pager.lastPage();
	});

	/**
	 * Fetch the previous page of a selection
	 * @event module:app#app:pager:prevPage
	 * @param {Netx.Collection} Netx collection instance
	 */
	dispatcher.on('app:pager:prevPage', function (pager, collection) {
		pager.prevPage();
	});

	/**
	 * Fetch the next page of a selection
	 * @event module:app#app:pager:nextPage
	 * @param {Netx.Collection} Netx collection instance
	 */
	dispatcher.on('app:pager:nextPage', function (pager, collection) {
		pager.nextPage();
	});

	/**
	 * Goto a specific page of a selection
	 * @event module:app#app:pager:gotoPage
	 * @param {Netx.Collection} Netx collection instance
	 */
	dispatcher.on('app:pager:gotoPage', function (pager, collection, page) {
		pager.gotoPage(page);
	});
	//============================================
	//	!App pager
	//============================================

	/**
	 * Logic for status polling
	 * @event module:app#app:pollBatchJob
	 * @param {string} uuid UUID string
	 * @param {event} event Event
	 * @param {boolean} hasResult Batch task has result
	 * @param {array} args Additional arguments to pass on (optional)
	 * @fires module:app#app:pollBatchJob
	 * @fires module:app#app:pollBatchJob:error
	 * @fires module:app#app:pollBatchJob:messages
	 * @fires module:app#app:pollBatchJob:success
	 * @fires module:share#share:simpleDownload
	 */
	dispatcher.on(
		'app:pollBatchJob',
		function (uuid, event, hasResult, args, pollInterval) {
			var batchJob = batchJobs.get(uuid);
			if (!batchJob) {
				var eventStrs = {};
				if (event) {
					eventStrs[event] = args;
				}
				batchJob = new BatchJobModel(
					{
						uuid: uuid,
						args: args || [],
						hasResult: hasResult,
					},
					{
						eventStrs: eventStrs,
						pollInterval,
					},
				);
				batchJobs.add(batchJob);
			} else {
				event && batchJob.addEventStr(event, args);
			}
		},
	);
	/**
	 * UI hander for batch job success:
	 */
	dispatcher.on(
		'app:pollBatchJob:success',
		function (uuid, event, hasResult, args) {
			//Netx.views.batchJobView && Netx.views.batchJobView.hide();
		},
	);

	/**
	 * UI hander for batch job error:
	 */
	dispatcher.on('app:pollBatchJob:error', function (err) {});
	/**
	 * Triggering app:stop will halt the application, log out the user and
	 * reload the page.  Triggered due to invalid session, expired session or logout.
	 * @event module:app#app:stop
	 */
	dispatcher.on('app:stop', function () {
		legacyWebsocket.disconnect();
		// Force page to reload
		//
		// (If you pass true as the value of the forceGet param - location.reload(forceGet)
		// Page will reload from server instead of cache
		// For more info see - http://www.w3schools.com/jsref/met_loc_reload.asp)
		// HACK https://jira.netx.net/browse/DAM-6124
		_.defer(function () {
			if (window.location.href.match(/request\/([0-9a-fA-F-]{36})/)) {
				window.location.href = window.location.href.substr(
					0,
					window.location.href.indexOf('#'),
				);
			} else {
				location.reload();
			}
		});
	});
	/**
	 * Keep us logged in if we regained consciousness in time to click that button:
	 * @event module:app#netx:session:timeout:confirmed
	 */
	dispatcher.on('netx:session:timeout:confirmed', function () {
		$('body').removeClass('session-expiring');
		clearTimeout(session.finalCountdown);
		delete session.finalCountdown;
		session.tickle();
	});
	/**
	 * When the session logout timer fires, we have only 60 seconds before our session times out!
	 * Alert the user!
	 * @event module:app#netx:session:timeoutWarning
	 * @fires module:app#app:confirm
	 */
	dispatcher.on('netx:session:timeoutWarning', function () {
		$('body').addClass('session-expiring');
		console.warn('Session timeout in 60 seconds.');
		dispatcher.trigger('app:confirm', {
			eventStr: 'netx:session:timeout',
			message: i18n.t('i18n.yourSessionIsAboutToExpire'),
			yesLabel: i18n.t('i18n.keepMeLoggedIn'),
			noLabel: '',
			title: i18n.t('i18n.timeoutWarning'),
		});
		// We're leaving together, but it's still farewell ...
		session.finalCountdown = setTimeout(function () {
			dispatcher.trigger('session:expire');
		}, 60 * 1000); // sixty seconds!
	});
	/**
	 * Handling session timeouts:
	 *
	 * The DAM cancels all sessions after a configurable period of time without
	 * user activity.  X7 keeps a session timer so we can warn the user if the
	 * timeout is imminent.  The timer is automatically reset on every API call.
	 *
	 * Prevent our client-side session timer from timing out during uploads,
	 * which are not API calls, and can potentially take longer than the timeout period.
	 * @event module:app#upload:progress
	 */
	dispatcher.on('upload:progress', function () {
		session.resetLogoutTimer();
	});

	/**
	 * RPC catastrophic error count reset - DAM-3261
	 * @event module:app#app:server:success
	 */
	dispatcher.on('app:server:success', function () {
		NS.set({serverUnresponsiveCount: 0});
	});
	/**
	 * RPC catastrophic error counting - DAM-3261
	 * @event module:app#app:server:success
	 * @fires module:app#app:alert
	 */
	dispatcher.on('app:server:error', function () {
		var count = NS.get('serverUnresponsiveCount') + 1;
		NS.set({serverUnresponsiveCount: count});
		if (count > 3) {
			dispatcher.trigger(
				'app:alert',
				i18n.t('i18n.serverNotAvailable', 'danger'),
			);
		}
	});

	/**
	 * Install analytics
	 * @event module:app#netx:analytics:start
	 * @param {string} provider Provider name
	 * @param {string} code Tracking code
	 * @param {function} cb Callback
	 */
	dispatcher.once('netx:analytics:start', function (provider, code, cb) {
		// Someday we can expand this to support multiple providers.  But for now, it's all Google:
		if (!['google'].includes(provider)) {
			console.warn(`unsupported analytics provider ${provider}`);
			return cb();
		}

		import(
			/* webpackChunkName: "google-analytics" */ '@netx/core/analytics/google'
		).then(({default: setupGoogleAnalytics}) => {
			setupGoogleAnalytics(code);
			cb();
		});
	});

	/**
	 * If the server version is incorrect, provide a warning.
	 * @event module:app#app:serverVersionWarning
	 * @fires module:app#app:confirm
	 * @global
	 */
	dispatcher.on(
		'app:serverVersionWarning',
		function (version, required_version) {
			dispatcher.trigger('app:confirm', {
				title: i18n.t('i18n.serverVersionWarningTitle'),
				message: i18n.t('i18n.serverVersionWarningMessage', {
					server_version: version,
					required_version: required_version,
				}),
				//"This application requires DAM version " + required_version + ", but this DAM is at version " + version + '.  If you continue, you may encouter compatability errors.  You may prefer to use <a href="/ImagePortal.jsp">the Flex interface</a> instead.',
				yesLabel: i18n.t('i18n.serverVersionWarningContinue'),
				noLabel: i18n.t('i18n.serverVersionWarningCancel'),
				eventStr: 'app:compatible',
				id: 'server-version-warning',
			});
		},
	);

	/**
	 * Display a menu as a set of option tiles, using OptionTilesView
	 *
	 * @event module:app#app:showOptions
	 * @param {Netx.Menu} menu
	 * @param {mixed} payload Additional data to send to options view and its child views ( best as an object ); this
	 * @param {string} viewKey A key for referencing the OptionTilesView (when triggering app:hideOptions); if not provided, the menu will be stringified as the key.
	 * will be returned as an extra argument to the triggered action handler.
	 * @see module:app#app:hideOptions
	 */
	dispatcher.on('app:showOptions', function (options) {
		var tileCollection = new OptionTilesCollection([], {
			menu: options.menu,
			data: options.data,
		});
		if (tileCollection.length) {
			// If there is only one option and we have a wrapper - just fire it off
			// If the action does not have a wrapper - there is not much that can be done since we do not know how to get the data to the event properly
			if (tileCollection.length === 1) {
				var tileOption = tileCollection.at(0),
					wrapper = tileOption && tileOption.get('wrapper');
				if (wrapper) {
					return dispatcher.trigger.apply(
						dispatcher,
						[tileOption.get('action')].concat(
							wrapper({data: tileOption.get('data')}),
						),
					);
				}
			}
			import(
				/* webpackChunkName: "view-optionTiles" */ './views/optionTilesView'
			).then((View) => {
				modalViews.add({
					viewClass: View.default,
					showActions: ['app:showOptions'],
					viewOptions: _.extend({}, _.omit(options, 'menu'), {
						collection: tileCollection,
					}),
				});
			});
		} else {
			console.warn('No valid actions found in menu.');
		}
	});


	/**
	 * app:start starts up the Netx skin/application
	 * Show the app layer, create views, etc.
	 * @event module:app#app:start
	 * @fires module:app#app:started
	 */
	dispatcher.on('app:start', function () {
		// Give any other `app:start` listener the ability to respond prior to calling `app:started`
		_.defer(function () {
			// Welcome to X7!  Please click responsibly.
			dispatcher.trigger('app:started');
		});
	});

	logger.silly('loading required modules...');
	dispatcher.on('app:load:modules', function (NS) {
		Promise.all([
			import(
				/* webpackMode: "eager"*/ /* webpackChunkName: "app-module-dependencies" */ '@netx/core-auth'
			),
			import(
				/* webpackMode: "eager"*/ /* webpackChunkName: "app-module-dependencies" */ '@netx/core-notification'
			),
		]).then((imports) => {
			logger.silly('loaded required modules');
			const [Auth, Notification] = imports;

			// Now initialize auth module
			dispatcher.trigger(
				'netx:load:module',
				[Auth.default, Notification.default],
				{
					initialized() {
						logger.silly('starting analytics...');
						// Setup analytics
						new Analytics().then(() => {
							logger.silly('initialized');
							dispatcher.trigger('app:extend', NS);
							dispatcher.trigger('app:initialized', NS);
						});
					},
				},
			);
		});
	});

	//=================================
	//	!EVENT LISTENERS
	//=================================
	logger.silly('bootstrapping...');
	$.when.apply($, NS.bootstrap).then(function () {
		logger.silly('bootstrapped');
		delete NS.bootstrap;
		dispatcher.trigger('app:load:modules', NS);
	});
});

export default {
	initialize(opts) {
		return new Promise((resolve, reject) => {
			dispatcher.once('app:initialized', () => {
				import(
					/* webpackMode: "eager"*/ /* webpackChunkName: "app-module-dependencies" */ './routes'
				).then((routes) => {
					resolve(routes.default);
				});
			});
			dispatcher.trigger('app:init');
		});
	},
};
